import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export const APPLICATION_POSTAL_CODE_ERROR =
  'Vul een geldige Nederlandse of Belgische postcode in (bijv. 2717 HA of 1000).';
export const APPLICATION_PHONE_ERROR =
  'Vul een geldig Nederlands telefoonnummer of Belgisch gsm-nummer in (bijv. 06 12345678 of +32 470 12 34 56).';

const DUTCH_POSTAL_CODE = /^([1-9]\d{3})\s?([A-Za-z]{2})$/;
const BELGIAN_POSTAL_CODE = /^[1-9]\d{3}$/;
const ALLOWED_PHONE_CHARACTERS = /^[0-9+().\s-]*$/;
const NATIONAL_PHONE = /^0[1-9]\d{8}$/;
const DUTCH_INTERNATIONAL_PHONE = /^(?:\+31|0031)0?([1-9]\d{8})$/;
const BELGIAN_INTERNATIONAL_GSM = /^(?:\+32|0032)0?(4\d{8})$/;

function trimmedString(value: unknown): string | null {
  return typeof value === 'string' ? value.trim() : null;
}

export function isSupportedPostalCode(value: unknown): boolean {
  const input = trimmedString(value);
  if (input === null) {
    return false;
  }
  if (!input) {
    return true;
  }
  if (BELGIAN_POSTAL_CODE.test(input)) {
    return true;
  }

  const dutchMatch = input.match(DUTCH_POSTAL_CODE);
  if (!dutchMatch) {
    return false;
  }

  return !['SA', 'SD', 'SS'].includes(dutchMatch[2].toUpperCase());
}

export function normalizePostalCode(value: unknown): string {
  const input = trimmedString(value) ?? '';
  if (!input || BELGIAN_POSTAL_CODE.test(input)) {
    return input;
  }

  const dutchMatch = input.match(DUTCH_POSTAL_CODE);
  return dutchMatch ? `${dutchMatch[1]} ${dutchMatch[2].toUpperCase()}` : input;
}

function compactPhone(value: unknown): string | null {
  const input = trimmedString(value);
  if (input === null || !ALLOWED_PHONE_CHARACTERS.test(input)) {
    return null;
  }
  return input.replace(/[\s().-]/g, '');
}

export function isSupportedPhoneNumber(value: unknown): boolean {
  const compact = compactPhone(value);
  if (compact === null) {
    return false;
  }
  if (!compact) {
    return true;
  }

  return (
    NATIONAL_PHONE.test(compact) ||
    DUTCH_INTERNATIONAL_PHONE.test(compact) ||
    BELGIAN_INTERNATIONAL_GSM.test(compact)
  );
}

export function normalizePhoneNumber(value: unknown): string {
  const compact = compactPhone(value) ?? '';
  if (!compact || NATIONAL_PHONE.test(compact)) {
    return compact;
  }

  const dutchMatch = compact.match(DUTCH_INTERNATIONAL_PHONE);
  if (dutchMatch) {
    return `+31${dutchMatch[1]}`;
  }

  const belgianMatch = compact.match(BELGIAN_INTERNATIONAL_GSM);
  if (belgianMatch) {
    return `+32${belgianMatch[1]}`;
  }

  return compact;
}

export const supportedPostalCodeValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null =>
  isSupportedPostalCode(control.value) ? null : { supportedPostalCode: true };

export const supportedPhoneNumberValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null =>
  isSupportedPhoneNumber(control.value) ? null : { supportedPhoneNumber: true };
